/**
 * Markdown完整性判断工具
 * 用于确保Markdown语法在打字效果中的完整性
 */

import { createDebugLogger, logProcessingInfo } from './utils/logUtils';
import { ProcessorFunction, LogDebugFunction } from './utils/markdownTypes';
import { getLogConfig, enableMarkdownLog, disableMarkdownLog } from './utils/logConfig';

// 导入各类处理器
import { 
  // 基础处理器
  processUnorderedList, 
  processOrderedList,
  processIndentedUnorderedList,
  processIndentedOrderedList,
  processBold,
  processItalic,
  processHeading,
  processBlockquote,
  processCodeBlock,
  processInlineCode,
  processLink,
  processImageLink,
  
  // 组合处理器
  processUnorderedListWithBold,
  processUnorderedListWithItalic,
  processOrderedListWithBold,
  processIndentedListWithBold,
  processIndentedListWithItalic,
  processIndentedOrderedListWithBold,
  
  // 特殊处理器
  processSpecialFormatting,
  processListAtLineStart,
  processIndentedListBoldAtLineStart
} from './processors';

/**
 * 按优先级顺序定义处理器组
 */
const PROCESSORS: {[key: string]: ProcessorFunction[]} = {
  // 特殊处理
  special: [
    processSpecialFormatting,
    processIndentedListBoldAtLineStart,
    processListAtLineStart
  ],
  
  // 复合语法处理
  composite: [
    // 列表与格式组合
    processUnorderedListWithBold,
    processUnorderedListWithItalic,
    processOrderedListWithBold,
    
    // 缩进列表与格式组合
    processIndentedListWithBold,
    processIndentedListWithItalic,
    processIndentedOrderedListWithBold
  ],
  
  // 列表处理
  list: [
    processIndentedUnorderedList,
    processIndentedOrderedList,
    processUnorderedList,
    processOrderedList
  ],
  
  // 块级元素处理
  block: [
    processHeading,
    processBlockquote,
    processCodeBlock,
    processInlineCode
  ],
  
  // 格式化元素处理
  formatting: [
    processBold,
    processItalic
  ],
  
  // 链接和图片处理
  link: [
    processLink,
    processImageLink
  ]
};

/**
 * 执行一组处理器函数，如果任一处理器返回结果大于0，则立即返回
 */
const runProcessors = (
  processors: ProcessorFunction[],
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  for (const processor of processors) {
    const result = processor(text, startIdx, charsToShow, analyzedText, remainingText, logDebugInfo);
    if (result > 0) return result;
  }
  return 0;
};

/**
 * 检测并保证Markdown语法的完整性
 * @param text 完整的文本
 * @param startIdx 开始索引
 * @param charsToShow 计划显示的字符数
 * @returns 需要额外显示的字符数
 */
export const ensureMarkdownIntegrity = (
  text: string, 
  startIdx: number, 
  charsToShow: number
): number => {
  if (startIdx >= text.length) return 0;
  
  const remainingText = text.substring(startIdx);
  const analyzedText = text.substring(0, startIdx + charsToShow);
  
  // 创建调试日志工具
  const logDebugInfo = createDebugLogger(remainingText, analyzedText, charsToShow, startIdx);
  
  // 记录基本处理信息
  logProcessingInfo(remainingText, analyzedText, startIdx);
  
  // 按照处理器组的顺序依次尝试处理
  const processorGroups = ['special', 'composite', 'list', 'block', 'formatting', 'link'];
  
  for (const group of processorGroups) {
    const result = runProcessors(
      PROCESSORS[group],
      text, 
      startIdx, 
      charsToShow, 
      analyzedText,
      remainingText,
      logDebugInfo
    );
    
    if (result > 0) return result;
  }
  
  return 0;
};

/**
 * 导出日志控制函数
 */
export {
  enableMarkdownLog,
  disableMarkdownLog,
  getLogConfig
}; 